import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Timeline, 
  Tag, 
  Button, 
  Statistic, 
  Row, 
  Col,
  Avatar,
  Spin,
  Empty,
  Divider
} from 'antd';
import {
  TrophyOutlined,
  VideoCameraOutlined,
  StarOutlined,
  ArrowLeftOutlined,
  YoutubeOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  UserOutlined
} from '@ant-design/icons';
import './DetailHistory.css';

const DetailHistory = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
        const loadUserHistory = async () => {
        setLoading(true);
        try {
        // Fetch data from JSON
        const response = await fetch('/jsons/data.json');
        const data = await response.json();

        // Find user
        const foundUser = data.leaderboard.find(u => u.nim_nip === userId);
        
        if (!foundUser) {
            setUser(null);
            setHistory([]);
            setLoading(false);
            return;
        }

        setUser(foundUser);

        // Get user's history
        const userHistory = data.history.filter(h => h.nim_nip === userId);
        
        // Generate dummy additional history for demonstration
        const dummyHistory = [
            ...userHistory,
            {
            id: `h${Math.random()}`,
            nim_nip: userId,
            video_title: 'Introduction to Machine Learning Algorithms',
            video_url: 'https://youtube.com/watch?v=demo1',
            points: 1,
            verified_date: '2025-01-08',
            verified_by: 'admin'
            },
            {
            id: `h${Math.random()}`,
            nim_nip: userId,
            video_title: 'Deep Learning with TensorFlow',
            video_url: 'https://youtube.com/watch?v=demo2',
            points: 1,
            verified_date: '2025-01-05',
            verified_by: 'admin'
            },
            {
            id: `h${Math.random()}`,
            nim_nip: userId,
            video_title: 'Data Science Best Practices',
            video_url: 'https://youtube.com/watch?v=demo3',
            points: 1,
            verified_date: '2025-01-03',
            verified_by: 'admin'
            }
        ];

        // Sort by date (newest first)
        const sortedHistory = dummyHistory.sort((a, b) => 
            new Date(b.verified_date) - new Date(a.verified_date)
        );

        setHistory(sortedHistory);
        } catch (error) {
        console.error('Error loading history:', error);
        } finally {
        setLoading(false);
        }
    };
    loadUserHistory();
  }, [userId]);

  const getRankColor = (rank) => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return 'bronze';
    return 'blue';
  };

  const getRankIcon = (rank) => {
    if (rank <= 3) return <TrophyOutlined />;
    return <StarOutlined />;
  };

  if (loading) {
    return (
      <div className="history-loading">
        <Spin size="large" tip="Memuat riwayat..." />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="history-container">
        <div className="history-content">
          <Card className="not-found-card">
            <Empty
              description="User tidak ditemukan"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
            <Button 
              type="primary" 
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/')}
              style={{ marginTop: 20 }}
            >
              Kembali ke Leaderboard
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // Find user's rank in leaderboard
  const userRank = 1; // This should be calculated based on actual leaderboard position

  return (
    <div className="history-container">
      <div className="history-header">
        <Button 
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-button"
        >
          Kembali
        </Button>
      </div>

      <div className="history-content">
        {/* User Profile Card */}
        <Card className="profile-card">
          <div className="profile-header">
            <Avatar 
              size={100} 
              src={user.avatar}
              icon={<UserOutlined />}
              className="profile-avatar"
            />
            <div className="profile-info">
              <h1>{user.name}</h1>
              <p className="nim-nip">{user.nim_nip}</p>
              <Tag 
                color={user.role === 'student' ? 'blue' : 'green'}
                className="role-tag"
              >
                {user.role === 'student' ? 'Mahasiswa' : 'Dosen'}
              </Tag>
            </div>
            <div className="rank-badge" style={{ borderColor: getRankColor(userRank) }}>
              {getRankIcon(userRank)}
              <span className="rank-text">Rank #{userRank}</span>
            </div>
          </div>

          <Divider />

          <Row gutter={[24, 24]} className="stats-row">
            <Col xs={24} sm={8}>
              <Statistic
                title="Total Poin"
                value={user.total_points}
                prefix={<StarOutlined />}
                valueStyle={{ 
                  color: '#14B8A6',
                  fontWeight: 700 
                }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Total Video"
                value={user.total_videos}
                prefix={<VideoCameraOutlined />}
                valueStyle={{ 
                  color: '#14B8A6',
                  fontWeight: 700 
                }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Rata-rata Poin"
                value={user.total_videos > 0 ? (user.total_points / user.total_videos).toFixed(1) : 0}
                suffix="/ video"
                valueStyle={{ 
                  color: 'rgb(255, 249, 175)',
                  fontWeight: 700,
                  textShadow: '0 0 10px rgba(255, 249, 175, 0.3)'
                }}
              />
            </Col>
          </Row>
        </Card>

        {/* History Timeline Card */}
        <Card className="history-card" title={
          <div className="card-title">
            <CalendarOutlined />
            <span>Riwayat Kontribusi</span>
          </div>
        }>
          {history.length === 0 ? (
            <Empty
              description="Belum ada riwayat kontribusi"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Timeline
              mode="left"
              className="history-timeline"
              items={history.map((item) => ({
                color: 'green',
                dot: <CheckCircleOutlined style={{ fontSize: 16 }} />,
                label: (
                  <div className="timeline-date">
                    {new Date(item.verified_date).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </div>
                ),
                children: (
                  <Card className="timeline-card" bordered={false}>
                    <div className="timeline-content">
                      <div className="video-icon-wrapper">
                        <YoutubeOutlined className="video-icon" />
                      </div>
                      <div className="timeline-details">
                        <h3 className="video-title">{item.video_title}</h3>
                        <div className="timeline-meta">
                          <Tag icon={<StarOutlined />} color="blue">
                            +{item.points} Poin
                          </Tag>
                          <Tag icon={<CheckCircleOutlined />} color="green">
                            Verified by {item.verified_by}
                          </Tag>
                        </div>
                        <Button
                          type="link"
                          icon={<YoutubeOutlined />}
                          onClick={() => window.open(item.video_url, '_blank')}
                          className="watch-button"
                        >
                          Tonton Video
                        </Button>
                      </div>
                    </div>
                  </Card>
                )
              }))}
            />
          )}
        </Card>
      </div>
    </div>
  );
};

export default DetailHistory;
